/**
 * ETag capture for If-Match flows.
 *
 * Generated orval functions return the parsed body only, so the mutator
 * records the response ETag against the returned object. Callers read it with
 * `etagOf(resource)` and send it back via `ifMatch(etag)`. ETags are never
 * synthesized client-side.
 */
import { ApiError } from "./errors";

const etags = new WeakMap<object, string>();

export function rememberEtag(body: unknown, response: Response): void {
  const etag = response.headers.get("ETag");
  if (etag && body && typeof body === "object") etags.set(body, etag);
}

export function etagOf(resource: unknown): string | undefined {
  return resource && typeof resource === "object" ? etags.get(resource) : undefined;
}

export function ifMatch(etag: string | null | undefined): Record<string, string> {
  return etag ? { "If-Match": etag } : {};
}

export function ifMatchFor(resource: unknown): RequestInit {
  return { headers: ifMatch(etagOf(resource)) };
}

/** The conflict the server sends for a stale version, raised locally. */
function staleVersion(): ApiError {
  return new ApiError({
    code: "conflict",
    message: "modified by someone else",
    status: 409,
    details: { reason: "stale_version" },
  });
}

/**
 * An action was confirmed on a list row; `fresh` was fetched right before
 * sending it. Throw the stale-version conflict (what the server would send)
 * unless `fresh` is still the version the user confirmed, so nothing acts on
 * content nobody saw. `version` picks what identifies a version.
 */
export function assertConfirmed<T>(fresh: T, confirmed: T, version: (x: T) => unknown): void {
  if (JSON.stringify(version(fresh)) !== JSON.stringify(version(confirmed))) throw staleVersion();
}

/**
 * If-Match for an action confirmed on a list row that carries no ETag of its
 * own: `fresh` supplies the ETag, but only if it is still the version the user
 * confirmed (same `updated_at`).
 */
export function ifMatchConfirmed(
  fresh: { updated_at: string },
  confirmed: { updated_at: string },
): Record<string, string> {
  assertConfirmed(fresh, confirmed, (x) => x.updated_at);
  return ifMatch(etagOf(fresh));
}
